import { FC } from "react";
import {
  IconArrowRight,
  IconCheck,
  IconClose,
  IconReturn,
  StepOne,
  StepThree,
  StepTwo,
} from "@idg-web-platform/pulse";
import classNames from "classnames";
import { VNode } from "preact";
import css from "../orderDetailsPage.module.css";
import { OrderShipment, returnedPartially } from "../entities/OrderShipment.ts";
import { StatusKind } from "../statusBadge.tsx";
import { renderLineItem } from "./renderLineItem.tsx";
import { ShipmentDocumentInfo } from "./shipmentDocumentInfo.tsx";
import { formatDate } from "../../utils/formats.ts";

interface ShipmentDetailProps {
  shipment: OrderShipment;
  index: number;
  shipmentsLength: number;
}

function stepIndicator(kind: StatusKind): VNode | null {
  switch (kind) {
    case StatusKind.Received:
      return <StepOne />;
    case StatusKind.Processed:
      return <StepTwo />;
    case StatusKind.Shipped:
    case StatusKind.Delivered:
      return <StepThree />;
    case StatusKind.Cancelled:
    case StatusKind.Returned:
      return null;
  }
}

function fallbackIcon(kind: StatusKind): [VNode, string | undefined] | null {
  switch (kind) {
    case StatusKind.Cancelled:
      return [<IconClose />, css.statusIconRed];
    case StatusKind.Returned:
      return [<IconReturn />, undefined];
    case StatusKind.Delivered:
      return [<IconCheck />, css.statusIconGreen];
    default:
      return null;
  }
}

export function statusLabel(kind: StatusKind): string {
  switch (kind) {
    case StatusKind.Received:
      return "EINGEGANGEN";
    case StatusKind.Processed:
      return "IN BEARBEITUNG";
    case StatusKind.Shipped:
      return "VERSENDET";
    case StatusKind.Delivered:
      return "ZUGESTELLT";
    case StatusKind.Returned:
      return "ZURÜCKGESENDET";
    case StatusKind.Cancelled:
      return "VON UNS STORNIERT";
  }
}

function showTrackingLink(kind: StatusKind): boolean {
  return kind === StatusKind.Shipped || kind === StatusKind.Delivered;
}

export const ShipmentStepper: FC<{ kind: StatusKind }> = ({ kind }) => {
  const indicator = stepIndicator(kind);
  if (indicator) return <span className={css.shipmentStepper}>{indicator}</span>;

  const fallback = fallbackIcon(kind);
  if (!fallback) return null;
  const [icon, color] = fallback;
  return <span className={classNames(css.statusIcon, color)}>{icon}</span>;
};

export const ShipmentDetail: FC<ShipmentDetailProps> = ({ shipment, index, shipmentsLength }) => {
  const kind = shipment.status.kind;
  const cancelled = kind === StatusKind.Cancelled;
  const carrier = shipment.carrier;
  const trackingVisible = showTrackingLink(kind) && Boolean(shipment.trackingUrl);
  const hasMeta = Boolean(carrier || shipment.sentBy || trackingVisible);

  return (
    <div className={classNames(css.shipment, cancelled && css.shipmentCancelled)}>
      <div className={css.shipmentHeader}>
        <span className={css.shipmentNumber}>
          PAKET {index + 1} / {shipmentsLength}
        </span>
        <ShipmentStepper kind={kind} />
      </div>

      <div className={css.shipmentInfoBand}>
        <div className={css.shipmentInfoStatus}>
          <span className={css.statusLabel}>{statusLabel(kind)}</span>
          {shipment.statusDate && (
            <span className={css.statusDate}>am {formatDate(shipment.statusDate)}</span>
          )}
        </div>
        {hasMeta && (
          <div className={css.shipmentInfoMeta}>
            {carrier && <span className={css.carrier}>Lieferung durch: {carrier}</span>}
            {shipment.sentBy && <span className={css.sentBy}>Von: {shipment.sentBy}</span>}
            {trackingVisible && (
              <a
                className={css.trackingLink}
                href={shipment.trackingUrl}
                target="_blank"
                rel="noreferrer"
              >
                Sendungsverfolgung
                <IconArrowRight />
              </a>
            )}
          </div>
        )}
      </div>

      {shipment.orderLineItems.map((line) =>
        renderLineItem(line, true, returnedPartially(shipment)),
      )}
      {shipment.document && <ShipmentDocumentInfo document={shipment.document} />}
    </div>
  );
};
